import React, { Component } from 'react';
import styled from 'styled-components/macro';
import I18n from '../../helper/Localization';
import { DefaultButton, FontIcon, getTheme, PrimaryButton } from 'office-ui-fabric-react';
import { mergeStyles, mergeStyleSets } from 'office-ui-fabric-react/lib/Styling';

const TestExecutionContainer = styled.div`
    display: flex;
    flex-direction: row;
    flex: 1;
`;

const LeftContainer = styled.div`
    display: flex;
    min-width: 440px;
    flex-direction: column;
    color: white;
    flex: 1;
    @media only screen and (max-width: 1800px) {
        min-width: 10px;
    }
`;

const RightContainer = styled.div`
    display: flex;
    flex-direction: column;
    justify-content: center;
    align-items: center;
    flex: 2;
`;

const ExplanationWrapper = styled.div`
    padding: 55px 70px 0 47px;
    @media only screen and (max-width: 1400px) {
        padding: 25px 40px 0 47px;
    }
`;

const HeaderContainer = styled.div`
    display: flex;
    flex-direction: row;
    font-weight: bold;
    font-size: 40px;
    @media only screen and (max-width: 1400px) {
        font-size: 23px;
    }
`;

const ExplanationContainer = styled.div`
    display: flex;
    flex-direction: row;
    padding-top: 20px;
    font-size: 18px;
    @media only screen and (max-width: 1400px) {
        font-size: 14px;
        padding-top: 10px;
    }
`;

const ButtonContainer = styled.div`
    margin-top: auto;
    padding: 30px 48px 47px 48px;
    display: flex;
    justify-content: center;
    align-items: center;
    flex-direction: column;
    min-width: max-content;
    @media only screen and (max-width: 1500px) {
        padding: 30px 15px 27px 15px;
    }
`;

const UserIconContainer = styled.div`
    display: flex;
    position: relative;
    flex-direction: column;
    margin-top: auto;
    padding: 10px;
    min-width: 430px;
    @media only screen and (max-width: 1400px) {
        min-width: 350px;
    }
`;

const MarginBottom = styled.div`
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 15px;
    width: 100%;
`;

const StyledList = styled.ol`
    padding-left: 15px;
`;

/**
 * the default gain value
 */
const defaultGainValue = 0.0125;
/**
 * the default frequency in herz
 */
const defaultFrequency = 500;
/**
 * the step size to decrease the current gain value
 */
const lowerGainDivisor = 1.41;

/**
 * view component that displays the test execution for left or right ear with or without hearing protection
 */
export class TestExecutionView extends Component {
    oscillator;
    gainNodeLeft;
    gainNodeRight;

    constructor(props) {
        super(props);
        this.interval = null;
        this.state = {
            ear: 'left',
            isTestActive: false,
            withHearingProtection: false,
            currentGainValue: defaultGainValue,
            isSoundPlaying: false,
            isMobile: window.innerWidth <= 1400,
        };
        this.oscillator = { current: null };
        this.gainNodeLeft = { current: null };
        this.gainNodeRight = { current: null };

        // Bind the resize handler
        this.handleResize = this.handleResize.bind(this);
    }

    // Resize event handler
    handleResize() {
        this.setState({ ...this.state, isMobile: window.innerWidth <= 1400 });
    }

    // Add event listener when the component mounts
    componentDidMount() {
        window.addEventListener('resize', this.handleResize);
    }

    // Remove event listener when the component unmounts
    componentWillUnmount() {
        window.removeEventListener('resize', this.handleResize);
    }

    // Define dynamic button styles based on window size
    beginButtonStyles = () => {
        const isMobile = this.state.isMobile;

        return {
            ...this.commonButtonStyles,
            root: {
                ...this.commonButtonStyles.root,
                color: 'white',
                border: '1px solid white',
                minWidth: isMobile ? '95px' : '195px', // Change based on screen size
                backgroundColor: this.theme.palette.white,
                padding: isMobile ? '8px' : '12px', // Adjust padding for mobile
            },
        };
    };

    abortButtonStyles = () => {
        const isMobile = this.state.isMobile;

        return {
            ...this.commonButtonStyles,
            root: {
                ...this.commonButtonStyles.root,
                border: 0,
                color: this.theme.palette.black,
                fontWeight: 'bold',
                minWidth: isMobile ? '95px' : '195px', // Smaller on mobile
                padding: isMobile ? '8px' : '12px',
            },
        };
    };

    theme = getTheme();

    soundIconClass = mergeStyles({
        fontSize: 32,
        height: 50,
        width: 50,
        margin: '0 auto',
        paddingLeft: 8,
    });
    classNames = mergeStyleSets({
        whiteIcon: [{ color: this.theme.palette.white }, this.soundIconClass],
    });

    commonButtonStyles = {
        root: {
            borderRadius: '4px',
            height: '39px',
            minHeight: '39px',
            fontSize: '15px',
        },
        rootHovered: {
            backgroundColor: 'none',
            color: 'none',
        },
        rootPressed: {
            backgroundColor: 'none',
            color: 'none',
        },
    };

    toneButtonStyles = {
        ...this.commonButtonStyles,
        root: {
            ...this.commonButtonStyles.root,
            boxShadow: '1px 2px 3px #00000079',
            border: 0,
            minWidth: '180px',
            fontSize: '23px',
            height: '50px',
            width: '250px',
            backgroundColor: this.theme.palette.themePrimary,
        },
        rootHovered: {
            backgroundColor: this.theme.palette.themeDarkAlt,
            color: "white",
        }
    };

    previousTestButtonStylesLeft = {
        ...this.commonButtonStyles,
        root: {
            ...this.commonButtonStyles.root,
            border: `1px solid ${this.theme.palette.white}`,
            color: this.theme.palette.white,
            fontWeight: 'bold',
            minWidth: '100%',
            backgroundColor: 'rgba(0, 0, 0, 0.0)',
        },
        icon: {
            marginBottom: 3,
        },
    };

    previousTestButtonStylesTestActive = {
        ...this.commonButtonStyles,
        root: {
            ...this.commonButtonStyles.root,
            border: `1px solid ${this.theme.palette.black}`,
            color: this.theme.palette.black,
            fontWeight: 'bold',
            minWidth: '100%',
            backgroundColor: 'rgba(0, 0, 0, 0.0)',
        },
        icon: {
            marginBottom: 3,
        },
    };

    /**
     * switch the current hearing test state to determine which heading is displayed
     */
    switchHeadline = () => {
        if (this.state.ear === 'left' && this.state.withHearingProtection) {
            return I18n.get().t('Test_Execution_LeftWithProtection');
        } else if (this.state.ear === 'left' && !this.state.withHearingProtection) {
            return I18n.get().t('Test_Execution_LeftWithoutProtection');
        } else if (this.state.ear === 'right' && this.state.withHearingProtection) {
            return I18n.get().t('Test_Execution_RightWithProtection');
        } else if (this.state.ear === 'right' && !this.state.withHearingProtection) {
            return I18n.get().t('Test_Execution_RightWithoutProtection');
        }
    };

    /**
     * calculates the gain ratio based on the max gain and the gain value at which the user can't hear the sound anymore
     * @param baseValue the max gain value
     * @param value the gain value at which the user can't hear the sound anymore
     */
    calcGainRatio = (baseValue, value) => {
        return value / baseValue;
    };

    /**
     * save the test result, reset the values and continue with the next test
     */
    finishCurrentTest = () => {
        this.oscillator.current?.stop();
        this.setState({ isSoundPlaying: false });
        // save result in test state
        this.props.dbHandler(this.state.ear, this.state.withHearingProtection, this.calcGainRatio(defaultGainValue, this.state.currentGainValue), () =>
            this.continueWithNextTest()
        );
        // change test status to finished
        this.setState({ isTestActive: false });
    };

    /**
     * helper function that takes the current test state and knows next test
     */
    continueWithNextTest = () => {
        this.setState({ currentGainValue: defaultGainValue });
        if (this.state.ear === 'left') {
            this.setState({ ear: 'right' });
        } else if (this.state.ear === 'right' && !this.state.withHearingProtection) {
            this.props.continue();
            this.setState({ ear: 'left', withHearingProtection: true });
        }
    };

    /**
     * function that is used when the "start test" button is pressed
     */
    callbackStartTest = () => {
        // set the test status to active
        this.setState({ isTestActive: true });
        this.playSineWave(this.state.ear, this.state.currentGainValue, defaultFrequency);
    };

    /**
     * Plays the test tone with a delay in form of a countdown.
     */
    playSineWave = (side, gain, frequency) => {
        this.setState({ isSoundPlaying: true });
        const audioContext = new window.AudioContext();
        this.oscillator.current = audioContext.createOscillator();

        this.gainNodeLeft.current = audioContext.createGain();
        this.gainNodeRight.current = audioContext.createGain();
        const merger = audioContext.createChannelMerger(2);

        this.oscillator.current.connect(this.gainNodeLeft.current);
        this.oscillator.current.connect(this.gainNodeRight.current);
        // connect channel merger to the left and right node
        this.gainNodeLeft.current.connect(merger, 0, 0);
        this.gainNodeRight.current.connect(merger, 0, 1);
        // connect the audio destination to the channel merger
        merger.connect(audioContext.destination);

        const ZERO = 0.000001;

        switch (side) {
            case 'left':
                // play sound on left ear
                this.gainNodeRight.current.gain.setValueAtTime(ZERO, audioContext.currentTime);
                this.gainNodeLeft.current.gain.setValueAtTime(gain, audioContext.currentTime);

                break;
            case 'right':
                // play sound on right ear
                this.gainNodeLeft.current.gain.setValueAtTime(ZERO, audioContext.currentTime);
                this.gainNodeRight.current.gain.setValueAtTime(gain, audioContext.currentTime);
                break;
            default:
                break;
        }

        this.oscillator.current.type = 'sine'; // Sine wave
        this.oscillator.current.frequency.setValueAtTime(frequency, audioContext.currentTime);

        this.oscillator.current.start();
    };

    stopSineWave = () => {
        this.oscillator.current?.stop();
        this.setState({ isSoundPlaying: false });
    };

    decreaseVolulme = (side) => {
        const newGainValue = this.state.currentGainValue / lowerGainDivisor;
        this.changeVolume(side, newGainValue);
    };

    increaseVolume = (side) => {
        const newGainValue = this.state.currentGainValue * lowerGainDivisor;
        this.changeVolume(side, newGainValue);
    };

    changeVolume = (side, newGainValue) => {
        if (this.oscillator.current) {
            switch (side) {
                case 'left':
                    if (this.gainNodeLeft) {
                        this.gainNodeLeft.current?.gain.setValueAtTime(newGainValue, this.oscillator.current.context.currentTime); // Reduce volume to 10%
                        this.setState({ currentGainValue: newGainValue });
                    }
                    break;
                case 'right':
                    if (this.gainNodeRight) {
                        this.gainNodeRight.current?.gain.setValueAtTime(newGainValue, this.oscillator.current.context.currentTime); // Reduce volume to 10%
                        this.setState({ currentGainValue: newGainValue });
                    }
                    break;
                default:
                    break;
            }
        }
    }

    /**
     * Function that is used when the "previous site" button ios pressed.
     */
    callbackPreviousTest = () => {
        this.setState({ currentGainValue: defaultGainValue, isTestActive: false });
        if (this.state.ear === 'right' && this.state.withHearingProtection === false) {
            this.setState({ ear: 'left' });
        } else if (this.state.ear === 'left' && this.state.withHearingProtection === true) {
            this.props.back();
            this.setState({ ear: 'right', withHearingProtection: false });
        } else if (this.state.ear === 'right' && this.state.withHearingProtection === true) {
            this.setState({ ear: 'left' });
        }
    };

    render() {
        const beginButtonStyles = this.beginButtonStyles();
        const abortButtonStyles = this.abortButtonStyles();

        return (
            <TestExecutionContainer>
                <LeftContainer
                    style={{
                        backgroundColor: this.state.isTestActive ? this.theme.palette.neutralLight : this.state.ear === 'left' ? this.theme.palette.blue : this.theme.palette.red,
                    }}
                >
                    <ExplanationWrapper>
                        <HeaderContainer>
                            <div dangerouslySetInnerHTML={{ __html: this.switchHeadline() }} />
                        </HeaderContainer>
                        {!this.state.isTestActive && (
                            <StyledList>
                                {this.state.withHearingProtection === true && (
                                    <li>
                                        <ExplanationContainer>{I18n.get().t('Test_Execution_One')}</ExplanationContainer>
                                    </li>
                                )}
                                <li>
                                    <ExplanationContainer>{I18n.get().t('Test_Execution_Two')}</ExplanationContainer>
                                </li>
                                <li>
                                    <ExplanationContainer>{I18n.get().t('Test_Execution_Three')}</ExplanationContainer>
                                </li>
                                <li>
                                    <ExplanationContainer>{I18n.get().t('Test_Execution_Four')}</ExplanationContainer>
                                </li>
                                <li>
                                    <ExplanationContainer>{I18n.get().t('Test_Execution_Five')}</ExplanationContainer>
                                </li>
                            </StyledList>
                        )}
                    </ExplanationWrapper>
                    <ButtonContainer>
                        <MarginBottom>
                            <DefaultButton
                                styles={abortButtonStyles}
                                style={{
                                    backgroundColor: this.state.isTestActive
                                        ? this.theme.palette.neutralLight
                                        : this.state.ear === 'left'
                                            ? this.theme.palette.blue
                                            : this.theme.palette.red,
                                    color: this.state.isTestActive ? this.theme.palette.black : this.theme.palette.white,
                                }}
                                text={I18n.get().t('Test_Instructions_Abort')}
                                onClick={this.props.abort}
                            />
                            {!this.state.isTestActive && (
                                <DefaultButton
                                    styles={beginButtonStyles}
                                    style={{
                                        color: this.state.isTestActive
                                            ? this.theme.palette.neutralLight
                                            : this.state.ear === 'left'
                                                ? this.theme.palette.blue
                                                : this.theme.palette.red,
                                    }}
                                    text={!this.state.isTestActive ? I18n.get().t('Test_Execution_BeginButton') : I18n.get().t('Test_Execution_TestIsActive')}
                                    onClick={() => this.callbackStartTest()}
                                    disabled={this.state.isTestActive}
                                />
                            )}
                        </MarginBottom>
                        {!(this.state.ear === 'left' && this.state.withHearingProtection === false) && (
                            <DefaultButton
                                styles={this.state.isTestActive ? this.previousTestButtonStylesTestActive : this.previousTestButtonStylesLeft}
                                iconProps={{ iconName: 'arrow-left-white' }}
                                disabled={this.state.isSoundPlaying}
                                text={I18n.get().t('TestExecution_PreviousTest')}
                                onClick={() => this.callbackPreviousTest()}
                            />
                        )}
                    </ButtonContainer>
                </LeftContainer>
                <RightContainer>
                    <UserIconContainer>
                        <FontIcon hidden={!(this.state.ear === 'left' && this.state.withHearingProtection)} iconName="active-left-filled" />
                        <FontIcon hidden={!(this.state.ear === 'left' && !this.state.withHearingProtection)} iconName="active-left-outlined" />
                        <FontIcon hidden={!(this.state.ear === 'right' && this.state.withHearingProtection)} iconName="active-right-filled" />
                        <FontIcon hidden={!(this.state.ear === 'right' && !this.state.withHearingProtection)} iconName="active-right-outlined" />
                    </UserIconContainer>
                    <ButtonContainer>
                        <MarginBottom>
                            <PrimaryButton
                                styles={this.toneButtonStyles}
                                style={{ marginRight: '26px' }}
                                text={I18n.get().t('Test_Execution_DecreaseVolumeButton')}
                                onClick={() => this.decreaseVolulme(this.state.ear)}
                                disabled={!this.state.isTestActive}
                            />
                            <PrimaryButton
                                styles={this.toneButtonStyles}
                                style={{ marginRight: '26px' }}
                                text={I18n.get().t('Test_Execution_IncreaseVolumeButton')}
                                onClick={() => this.increaseVolume(this.state.ear)}
                                disabled={!this.state.isTestActive}
                            />
                            <PrimaryButton
                                styles={this.toneButtonStyles}
                                text={I18n.get().t('Test_Execution_ToneNotHeardButton')}
                                onClick={() => this.finishCurrentTest()}
                                disabled={!this.state.isTestActive}
                            />
                        </MarginBottom>
                    </ButtonContainer>
                </RightContainer>
            </TestExecutionContainer>
        );
    }
}
